import { useEffect, useState } from 'react';
import { IconButton, Button } from '../../ds';
import { useChrome } from '../chrome';
import { useWidgetEdit } from '../widgets';
import { agoLabel } from '../../lib/time';

interface Props {
  label: string;
  syncedAt?: string;
  onNewBuild: () => void;
  onOpenPalette: () => void;
  /** The live notifications bell (composed upstream with the shared slices). */
  bell: React.ReactNode;
}

/** Recomputes the "updated Ns ago" label once a second from the freshest sync. */
function useTick(): number {
  const [, set] = useState(0);
  useEffect(() => {
    const h = setInterval(() => set((n) => n + 1), 1000);
    return () => clearInterval(h);
  }, []);
  return 0;
}

export function Topbar({ label, syncedAt, onNewBuild, onOpenPalette, bell }: Props) {
  const { theme, toggleTheme } = useChrome();
  const { editing, setEditing, gridSurface } = useWidgetEdit();
  useTick();
  const isDark = theme === 'dark';

  return (
    <header
      className="mc-topbar"
      style={{ height: 'var(--shell-topbar)', flex: 'none', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12, background: 'var(--translucent)', backdropFilter: 'var(--blur-panel)', WebkitBackdropFilter: 'var(--blur-panel)', zIndex: 30 }}
    >
      <h1 style={{ fontSize: 17, fontWeight: 600, letterSpacing: '-0.01em', margin: 0, flex: 'none' }}>{label}</h1>

      {/* On mobile the search is hidden; this spacer keeps the actions right-aligned. */}
      <div className="mc-only-mobile" style={{ flex: 1 }} />

      {/* The "/" search is the command palette now — this is its click target. */}
      <button
        className="mc-hide-mobile"
        onClick={onOpenPalette}
        aria-label="Open command palette"
        aria-haspopup="dialog"
        style={{ display: 'flex', alignItems: 'center', gap: 9, flex: 1, minWidth: 0, maxWidth: 400, padding: '8px 13px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'var(--bg)', cursor: 'pointer', textAlign: 'left' }}
      >
        <span style={{ display: 'flex', color: 'var(--muted)', flex: 'none' }} aria-hidden="true">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <circle cx={11} cy={11} r={7} />
            <path d="m21 21-4.3-4.3" />
          </svg>
        </span>
        <span style={{ flex: 1, minWidth: 0, color: 'var(--muted)', fontFamily: 'var(--font-body)', fontSize: 13.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          Jump to a surface, run an action…
        </span>
        <kbd style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--muted)', border: '1px solid var(--border)', borderRadius: 5, padding: '1px 5px' }}>/</kbd>
      </button>

      <span className="mc-hide-mobile" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--muted)', whiteSpace: 'nowrap', flex: 'none' }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--accent)', boxShadow: '0 0 8px var(--accent)', animation: 'ct-pulse 2s ease-in-out infinite' }} />
        updated {agoLabel(syncedAt)}
      </span>

      {/* Customize widgets — only on surfaces hosting a widget grid. */}
      {gridSurface && (
        <IconButton variant="ghost" label={editing ? 'Done customizing widgets' : 'Customize widgets'} active={editing} onClick={() => setEditing(!editing)}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <rect x={3} y={3} width={7} height={7} rx={1.5} />
            <rect x={14} y={3} width={7} height={7} rx={1.5} />
            <rect x={3} y={14} width={7} height={7} rx={1.5} />
            <path d="M17.5 14v7M14 17.5h7" />
          </svg>
        </IconButton>
      )}

      <IconButton variant="ghost" label="Toggle theme" onClick={toggleTheme}>
        {isDark ? (
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <circle cx={12} cy={12} r={4} />
            <path d="M12 2v2M12 20v2M4 12H2M22 12h-2M5.6 5.6 4.2 4.2M19.8 19.8l-1.4-1.4M18.4 5.6l1.4-1.4M4.2 19.8l1.4-1.4" />
          </svg>
        ) : (
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12.8A9 9 0 1 1 11.2 3 7 7 0 0 0 21 12.8Z" />
          </svg>
        )}
      </IconButton>

      {bell}

      <Button size="sm" onClick={onNewBuild} aria-label="New build — ask the crew">
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 5v14M5 12h14" />
          </svg>
          <span className="mc-hide-mobile">New build</span>
        </span>
      </Button>
    </header>
  );
}
